import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { SubPageLayout } from '../components/SubPageLayout';
import { useSeo } from '../lib/seo';
import { findArticleBySlug } from '../data/blogArticles';
import { getSlug } from '../lib/blog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(() => findArticleBySlug(slug));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setArticle(findArticleBySlug(slug));
    setNotFound(false);
    fetch(`${API}/blog`)
      .then((r) => r.json())
      .then((d) => {
        if (!active) return;
        const live = (d.articles || []).find((a) => getSlug(a) === slug);
        if (live) {
          setArticle((prev) => ({ ...(prev || {}), ...live, slug }));
        } else if (!findArticleBySlug(slug)) {
          setNotFound(true);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [slug]);

  const canonical = `https://smart-post-solution.de/blog/${slug}`;

  useSeo({
    title: article
      ? `${article.title} | Smart Post Solution`
      : notFound
        ? 'Artikel nicht gefunden | Smart Post Solution'
        : 'Blog | Smart Post Solution',
    description: article ? article.metaDescription || article.summary || '' : '',
    canonical,
    noindex: !article && notFound,
  });

  if (!article && notFound) {
    return (
      <SubPageLayout>
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24 text-center" data-testid="blog-article-notfound">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Artikel nicht gefunden</h1>
          <p className="text-slate-600 mb-8">
            Der gewünschte Beitrag existiert nicht oder wurde verschoben.
          </p>
          <Link to="/#blog" className="text-slate-800 underline hover:text-slate-600 font-medium">
            Zur Blog-Übersicht
          </Link>
        </div>
      </SubPageLayout>
    );
  }

  if (!article) {
    return (
      <SubPageLayout>
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24 flex justify-center">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin"></div>
        </div>
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout>
      <article className="max-w-3xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <Link
          to="/#blog"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-8 group"
          data-testid="blog-back-button"
        >
          <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>
        <h1
          className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8"
          data-testid="blog-article-title"
        >
          {article.title}
        </h1>
        <div
          className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-line"
          data-testid="blog-article-content"
        >
          {article.content}
        </div>
      </article>
    </SubPageLayout>
  );
}
